import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Building2, CalendarDays, Download, Eye, IndianRupee, Lock, Plus, RefreshCw, Save, Search, Trash2, Unlock, X } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const getCurrentPeriodKey = () => {
    const date = new Date();
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const formatInr = (value) =>
    Number(value || 0).toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    });

const parseMoney = (value) => {
    const next = Number(String(value || "").replace(/,/g, ""));
    return Number.isFinite(next) ? next : 0;
};

const newRowId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const DEFAULT_FINANCE = {
    investmentPool: 0,
    teamAllocationFund: 0,
    telemarketingIncome: 0,
    digitalMarketingSpend: 0,
    notes: "",
    financeRemarks: {},
    closeChecklist: {},
    departmentAccounts: [],
    otherExpenditures: [],
    teamAllocations: [],
    generatedInvoices: [],
    auditTrail: [],
    isLocked: false,
};

const DEFAULT_COMMAND = {
    investmentPool: 0,
    teamAllocationFund: 0,
    remainingBalance: 0,
    telemarketingIncome: 0,
    digitalMarketingSpend: 0,
    salaryPayout: 0,
    otherExpenditures: 0,
    pendingClaims: 0,
    approvedClaims: 0,
    profitLoss: 0,
    totalSpend: 0,
    generatedRevenue: 0,
};

const teamSpend = (row) =>
    Number(row.marketingSpend || 0)
    + Number(row.expenseClaims || 0)
    + Number(row.salary || 0)
    + Number(row.otherSpend || 0);

const teamBalance = (row) =>
    Number(row.allocation || 0)
    + Number(row.generated || 0)
    - teamSpend(row);

const cleanExpenseForExport = (item = {}) => ({
    Vendor: item.vendorName || "",
    Expense: item.expense || "",
    Amount: Number(item.amount || 0),
    "Spend Date": item.spendDate ? new Date(item.spendDate).toLocaleDateString("en-IN") : "",
    "Due Date": item.dueDate ? new Date(item.dueDate).toLocaleDateString("en-IN") : "",
    "Paid Date": item.paidDate ? new Date(item.paidDate).toLocaleDateString("en-IN") : "",
    "Approval Status": item.approvalStatus || "Draft",
    "Payment Status": item.paymentStatus || "Pending",
    "Payment Method": item.paymentMethod || "",
    "Bill File": item.billName || "",
    "Payment Proof": item.paymentProofName || "",
    Note: item.note || "",
});

const appendFinanceReportSheet = ({ workbook, sheetName, periodLabel, command, teamRows, pendingPayments, remarks }) => {
    const pendingTotal = pendingPayments.reduce((sum, item) => sum + Number(item.Amount || 0), 0);
    const rows = [
        ["Monthly Finance Report"],
        ["Month", periodLabel],
        [],
        ["Summary"],
        ["Income", command.generatedRevenue],
        ["Expense", command.totalSpend],
        ["Profit / Loss", command.profitLoss],
        ["Team Amounts", command.teamAllocationFund],
        ["Pending Payments", pendingTotal],
        [],
        ["Team-wise Budget"],
        ["Team", "Budget", "Actual Spend", "Balance"],
        ...teamRows.map((row) => [row.Department, row.Budget, row["Actual Spend"], row.Balance]),
        [],
        ["Pending Payments"],
        ["Vendor", "Expense", "Amount", "Due Date", "Payment Status", "Approval Status"],
        ...(pendingPayments.length ? pendingPayments.map((item) => [
            item.Vendor,
            item.Expense,
            item.Amount,
            item["Due Date"],
            item["Payment Status"],
            item["Approval Status"],
        ]) : [["No pending payments", "", "", "", "", ""]]),
        [],
        ["Remarks"],
        ["Why profit/loss happened", remarks?.profitLossReason || "-"],
        ["Pending payments note", remarks?.pendingPayments || "-"],
        ["Next month expected expense", remarks?.nextMonthExpectedExpense || "-"],
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet["!cols"] = [
        { wch: 28 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
};

const pdfSourceToBlobUrl = (source) => {
    if (!source) return "";
    if (!String(source).startsWith("data:application/pdf")) return source;

    const [meta, payload] = String(source).split(",");
    const mime = meta.match(/data:(.*?);base64/)?.[1] || "application/pdf";
    const binary = window.atob(payload || "");
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
};

const FinanceCommand = () => {
    const [periodKey, setPeriodKey] = useState(getCurrentPeriodKey());
    const [data, setData] = useState(null);
    const [message, setMessage] = useState("");
    const [vaultSearch, setVaultSearch] = useState("");
    const [lockBusy, setLockBusy] = useState(false);
    const [pdfPreview, setPdfPreview] = useState(null);
    const [allocationRows, setAllocationRows] = useState([]);
    const [allocationSaving, setAllocationSaving] = useState(false);

    useEffect(() => {
        document.title = "Finance Command | Savrion Technologies";
        return () => {
            if (pdfPreview?.objectUrl) URL.revokeObjectURL(pdfPreview.objectUrl);
        };
    }, [pdfPreview?.objectUrl]);

    const fetchFinance = async () => {
        try {
            const res = await api.get("/api/admin/finance/dashboard", {
                cache: false,
                params: { periodKey, _t: Date.now() },
            });
            setData(res.data);
            setAllocationRows((res.data?.finance?.teamAllocations || []).map((row) => ({
                id: row._id || newRowId(),
                team: row.team || "",
                amount: String(row.amount || ""),
                approvalStatus: row.approvalStatus || "Draft",
                note: row.note || "",
            })));
            setMessage("");
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to load finance command center.");
        } finally {
            // Data reloads automatically when month changes or after admin saves.
        }
    };

    useEffect(() => {
        document.title = "Finance Command | Savrion Technologies";
        fetchFinance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodKey]);

    const finance = { ...DEFAULT_FINANCE, ...(data?.finance || {}) };
    const command = { ...DEFAULT_COMMAND, ...(data?.commandCenter || {}) };
    const workspace = data?.financeWorkspace || {};
    const advanced = workspace.advancedFinance || {};
    const departments = workspace.departmentAccounts || [];
    const otherExpenditures = finance.otherExpenditures || [];
    const profitability = workspace.departmentProfitability || departments.map((row) => ({
        ...row,
        balance: teamBalance(row),
        totalSpend: teamSpend(row),
    }));
    const attachments = useMemo(() => {
        const q = vaultSearch.trim().toLowerCase();
        return (workspace.attachments || []).filter((item) => {
            if (!q) return true;
            return [item.title, item.type, item.owner, item.fileName, item.amount]
                .some((value) => String(value || "").toLowerCase().includes(q));
        });
    }, [vaultSearch, workspace.attachments]);

    const pendingClaims = workspace.paymentTracker?.items?.filter((item) => String(item.status).toLowerCase() === "pending") || [];
    const allocationTotal = allocationRows.reduce((sum, row) => sum + parseMoney(row.amount), 0);

    const addAllocationRow = () => {
        setAllocationRows((current) => [...current, { id: newRowId(), team: "", amount: "", note: "" }]);
    };

    const updateAllocationRow = (id, field, value) => {
        setAllocationRows((current) => current.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    };

    const removeAllocationRow = (id) => {
        setAllocationRows((current) => current.filter((row) => row.id !== id));
    };

    const saveAllocations = async () => {
        if (finance.isLocked) {
            setMessage("This finance month is locked. Unlock it before changing team amounts.");
            return;
        }
        setAllocationSaving(true);
        try {
            await api.put("/api/admin/finance/month", {
                periodKey,
                investmentPool: finance.investmentPool || 0,
                telemarketingIncome: finance.telemarketingIncome || 0,
                digitalMarketingSpend: finance.digitalMarketingSpend || 0,
                notes: finance.notes || "",
                financeRemarks: finance.financeRemarks || {},
                closeChecklist: finance.closeChecklist || {},
                departmentAccounts: finance.departmentAccounts || [],
                otherExpenditures: finance.otherExpenditures || [],
                generatedInvoices: finance.generatedInvoices || [],
                teamAllocations: allocationRows.map((row) => ({
                    team: row.team,
                    amount: parseMoney(row.amount),
                    approvalStatus: row.approvalStatus,
                    note: row.note,
                })),
            });
            setMessage("Team amounts saved for this month.");
            await fetchFinance();
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to save team amounts.");
        } finally {
            setAllocationSaving(false);
        }
    };

    const exportExcel = () => {
        const workbook = XLSX.utils.book_new();
        const pendingPayments = otherExpenditures
            .filter((item) => String(item.paymentStatus || "").toLowerCase() !== "paid")
            .map((item) => ({
                Vendor: item.vendorName || "-",
                Expense: item.expense || "-",
                Amount: Number(item.amount || 0),
                "Due Date": item.dueDate ? new Date(item.dueDate).toLocaleDateString("en-IN") : "-",
                "Payment Status": item.paymentStatus || "Pending",
                "Approval Status": item.approvalStatus || "Draft",
            }));
        const teamBudgetRows = departments.map((row) => ({
            Department: row.department,
            Budget: row.allocation,
            "Actual Spend": teamSpend(row),
            Balance: teamBalance(row),
        }));
        appendFinanceReportSheet({
            workbook,
            sheetName: "Monthly Report",
            periodLabel: data?.period?.payMonth || periodKey,
            command,
            teamRows: teamBudgetRows,
            pendingPayments,
            remarks: finance.financeRemarks || {},
        });
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(teamBudgetRows), "Team Budget");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(pendingPayments), "Pending Payments");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(profitability), "Profitability");
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(otherExpenditures.map(cleanExpenseForExport)), "All Expenses");
        XLSX.writeFile(workbook, `monthly_finance_report_${periodKey}.xlsx`);
    };

    const exportPdfReport = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let y = 16;
        const pendingPayments = otherExpenditures.filter((item) => String(item.paymentStatus || "").toLowerCase() !== "paid");
        const pendingTotal = pendingPayments.reduce((sum, item) => sum + Number(item.amount || 0), 0);
        const addLine = (label, value) => {
            doc.setFont(undefined, "bold");
            doc.text(label, 16, y);
            doc.setFont(undefined, "normal");
            doc.text(String(value), pageWidth - 16, y, { align: "right" });
            y += 8;
        };
        const checkPage = () => {
            if (y > 280) {
                doc.addPage();
                y = 16;
            }
        };

        doc.setFontSize(18);
        doc.setFont(undefined, "bold");
        doc.text("Monthly Finance Report", pageWidth / 2, y, { align: "center" });
        y += 8;
        doc.setFontSize(10);
        doc.setFont(undefined, "normal");
        doc.text(data?.period?.payMonth || periodKey, pageWidth / 2, y, { align: "center" });
        y += 14;

        doc.setFontSize(12);
        addLine("Income", formatInr(command.generatedRevenue));
        addLine("Expense", formatInr(command.totalSpend));
        addLine("Profit / Loss", formatInr(command.profitLoss));
        addLine("Team Amounts", formatInr(command.teamAllocationFund));
        addLine("Pending Payments", formatInr(pendingTotal));

        y += 6;
        doc.setFont(undefined, "bold");
        doc.text("Team-wise Budget", 16, y);
        y += 8;
        doc.setFontSize(9);
        departments.slice(0, 20).forEach((row) => {
            checkPage();
            doc.setFont(undefined, "bold");
            doc.text(row.department || "-", 16, y);
            doc.setFont(undefined, "normal");
            doc.text(`Budget ${formatInr(row.allocation)} | Spend ${formatInr(teamSpend(row))} | Balance ${formatInr(teamBalance(row))}`, 64, y);
            y += 7;
        });

        y += 6;
        checkPage();
        doc.setFontSize(12);
        doc.setFont(undefined, "bold");
        doc.text("Pending Payments", 16, y);
        y += 8;
        doc.setFontSize(9);
        if (pendingPayments.length) {
            pendingPayments.slice(0, 24).forEach((item) => {
                checkPage();
                doc.setFont(undefined, "bold");
                doc.text(item.vendorName || "Vendor", 16, y);
                doc.setFont(undefined, "normal");
                doc.text(`${item.expense || "-"} | ${formatInr(item.amount)} | ${item.paymentStatus || "Pending"}`, 64, y);
                y += 7;
            });
        } else {
            doc.setFont(undefined, "normal");
            doc.text("No pending payments.", 16, y);
        }

        doc.save(`monthly_finance_report_${periodKey}.pdf`);
    };

    const openDocument = (item) => {
        if (item.url) {
            const source = pdfSourceToBlobUrl(item.url);
            setPdfPreview({
                title: item.fileName || item.title || "Finance PDF",
                url: source,
                objectUrl: source !== item.url ? source : "",
            });
            return;
        }
        setMessage("This document is linked by record. Open from the original expense or invoice record when no file URL is stored.");
    };

    const closePdfPreview = () => {
        setPdfPreview((current) => {
            if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
            return null;
        });
    };

    const toggleMonthLock = async () => {
        const nextLocked = !finance.isLocked;
        setLockBusy(true);
        try {
            await api.patch("/api/admin/finance/month/lock", { periodKey, isLocked: nextLocked });
            setMessage(nextLocked ? "Finance month locked." : "Finance month unlocked.");
            await fetchFinance();
        } catch (error) {
            setMessage(error.response?.data?.message || "Unable to update month lock.");
        } finally {
            setLockBusy(false);
        }
    };

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 overflow-hidden">
                <section className="mx-auto max-w-[1800px] px-3 py-4 md:px-5">
                    <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <span className="text-[11px] font-black uppercase tracking-[0.22em] text-[var(--accent-primary)]">Finance command center</span>
                                <h1 className="mt-1.5 text-2xl font-black tracking-tight md:text-3xl">Monthly finance operating view</h1>
                                <p className="mt-1 text-sm font-semibold text-[var(--text-secondary)]">
                                    Investment, team funds, revenue, spend, approved claims, pending claims, and P/L.
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <label className="relative">
                                    <input type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} className="h-11 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 pr-10 text-sm font-black outline-none" />
                                    <CalendarDays size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                                </label>
                                <button type="button" onClick={exportExcel} className="inline-flex h-11 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 text-sm font-black">
                                    <Download size={16} />
                                    Excel Report
                                </button>
                                <button type="button" onClick={exportPdfReport} className="inline-flex h-11 items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 text-sm font-black">
                                    <Download size={16} />
                                    PDF Report
                                </button>
                                <button type="button" onClick={toggleMonthLock} disabled={lockBusy} className={`inline-flex h-11 items-center gap-2 rounded-xl px-4 text-sm font-black text-white disabled:opacity-60 ${finance.isLocked ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-950 hover:bg-slate-800 dark:bg-[var(--card-bg)] dark:text-slate-950"}`}>
                                    {finance.isLocked ? <Unlock size={16} /> : <Lock size={16} />}
                                    {finance.isLocked ? "Unlock Month" : "Lock Month"}
                                </button>
                            </div>
                        </div>
                    </section>

                    {message && (
                        <div className="mt-4 rounded-xl border border-[var(--accent-soft)] bg-[var(--accent-light)] px-4 py-3 text-sm font-black text-violet-800 dark:border-[var(--border-color)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]">
                            {message}
                        </div>
                    )}

                    <section className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                        <MetricCard label="Investment Pool" value={formatInr(command.investmentPool)} tone="violet" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Team Allocation Total" value={formatInr(command.teamAllocationFund)} tone="slate" icon={<Building2 size={18} />} />
                        <MetricCard label="Remaining Balance" value={formatInr(command.remainingBalance)} tone="emerald" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Telemarketing Income" value={formatInr(command.telemarketingIncome)} tone="emerald" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Digital Marketing Spend" value={formatInr(command.digitalMarketingSpend)} tone="amber" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Salary Spend" value={formatInr(command.salaryPayout)} tone="red" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Other Expenses" value={formatInr(command.otherExpenditures)} tone="red" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Pending Expense Claims" value={formatInr(command.pendingClaims)} sub={`${data?.metrics?.pendingExpenses || 0} pending`} tone="red" icon={<AlertTriangle size={18} />} />
                        <MetricCard label="Approved Claims Deducted" value={formatInr(command.approvedClaims)} tone="red" icon={<IndianRupee size={18} />} />
                        <MetricCard label="Profit / Loss" value={formatInr(command.profitLoss)} tone="emerald" icon={<IndianRupee size={18} />} />
                    </section>

                    <MonthlyMoneyChart command={command} />

                    <AdvancedFinanceOverview advanced={advanced} />

                    <section className="mt-4 grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
                        <Panel title="Team Fund Monitor">
                            <AdminAllocationEditor
                                rows={allocationRows}
                                total={allocationTotal}
                                locked={finance.isLocked}
                                saving={allocationSaving}
                                onAdd={addAllocationRow}
                                onUpdate={updateAllocationRow}
                                onRemove={removeAllocationRow}
                                onSave={saveAllocations}
                            />
                            <div className="mb-4 rounded-2xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
                                <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <h3 className="text-sm font-black">All team allocations</h3>
                                        <p className="text-xs font-bold text-[var(--text-secondary)]">
                                            Every team is listed here. Teams without finance allocation show {formatInr(0)}.
                                        </p>
                                    </div>
                                    <span className="rounded-full bg-[var(--accent-light)] px-3 py-1 text-xs font-black text-[var(--heading-secondary)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]">
                                        {departments.length} teams
                                    </span>
                                </div>
                                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                    {departments.length ? departments.map((row) => (
                                        <div key={row.department} className="rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-3 py-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="truncate text-xs font-black">{row.department || "Unassigned"}</span>
                                                <strong className={Number(row.allocation || 0) > 0 ? "text-emerald-600" : "text-[var(--text-secondary)]"}>
                                                    {formatInr(row.allocation)}
                                                </strong>
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="rounded-xl border border-dashed border-[var(--border-color)] px-4 py-6 text-sm font-bold text-[var(--text-secondary)]">
                                            No teams found for this month.
                                        </p>
                                    )}
                                </div>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-[760px] w-full text-left text-xs">
                                    <thead className="text-[var(--text-secondary)]">
                                        <tr className="border-b border-[var(--border-color)]">
                                            {["Department", "Allocation", "Revenue", "Expenses", "Balance"].map((head) => (
                                                <th key={head} className="px-3 py-3 font-black uppercase">{head}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {departments.map((row) => (
                                            <tr key={row.department} className="border-b border-[var(--border-color)]/70">
                                                <td className="px-3 py-3 font-black">{row.department}</td>
                                                <td className="px-3 py-3 font-black">{formatInr(row.allocation)}</td>
                                                <td className="px-3 py-3 font-black text-emerald-600">{formatInr(row.generated)}</td>
                                                <td className="px-3 py-3 font-black">{formatInr(teamSpend(row))}</td>
                                                <td className={`px-3 py-3 font-black ${teamBalance(row) < 0 ? "text-red-600" : "text-emerald-600"}`}>{formatInr(teamBalance(row))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </Panel>

                        <Panel title="Pending Finance Actions">
                            <PendingBox title="Pending expense claims" items={pendingClaims} />
                        </Panel>
                    </section>

                    <Panel title="Profitability By Department" className="mt-4">
                        <div className="overflow-x-auto">
                            <table className="min-w-[1100px] w-full text-left text-xs">
                                <thead className="text-[var(--text-secondary)]">
                                    <tr className="border-b border-[var(--border-color)]">
                                        {["Department", "Allocation", "Revenue", "Payroll Cost", "Claims", "Marketing", "Other Spend", "P/L"].map((head) => (
                                            <th key={head} className="px-3 py-3 font-black uppercase">{head}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {profitability.map((row) => (
                                        <tr key={row.department} className="border-b border-[var(--border-color)]/70">
                                            <td className="px-3 py-3 font-black">{row.department}</td>
                                            <td className="px-3 py-3 font-black">{formatInr(row.allocation)}</td>
                                            <td className="px-3 py-3 font-black text-emerald-600">{formatInr(row.generated)}</td>
                                            <td className="px-3 py-3">
                                                <span className="block font-black">{formatInr(row.salary)}</span>
                                                <span className="text-[10px] font-bold text-[var(--text-secondary)]">{Number(row.employeeCount || 0)} employee(s)</span>
                                            </td>
                                            <td className="px-3 py-3 font-black">{formatInr(row.expenseClaims)}</td>
                                            <td className="px-3 py-3 font-black">{formatInr(row.marketingSpend)}</td>
                                            <td className="px-3 py-3 font-black">{formatInr(row.otherSpend)}</td>
                                            <td className={`px-3 py-3 font-black ${Number(row.balance || 0) < 0 ? "text-red-600" : "text-emerald-600"}`}>{formatInr(row.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Finance Added Other Expenditures" className="mt-4">
                        <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <p className="text-sm font-bold text-[var(--text-secondary)]">
                                Other expenses entered by the finance team for this finance month.
                            </p>
                            <span className="rounded-full bg-red-50 px-3 py-1 text-xs font-black text-red-700 dark:bg-red-950/30 dark:text-red-200">
                                Total {formatInr(otherExpenditures.reduce((sum, item) => sum + Number(item.amount || 0), 0))}
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-[920px] w-full text-left text-xs">
                                <thead className="text-[var(--text-secondary)]">
                                    <tr className="border-b border-[var(--border-color)]">
                                        {["Vendor", "Expense", "Amount", "Due Date", "Paid Date", "Approval", "Payment", "Proof", "Note"].map((head) => (
                                            <th key={head} className="px-3 py-3 font-black uppercase">{head}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {otherExpenditures.length ? otherExpenditures.map((item, index) => (
                                        <tr key={`${item.expense}-${index}`} className="border-b border-[var(--border-color)]/70">
                                            <td className="px-3 py-3 font-black">{item.vendorName || "-"}</td>
                                            <td className="px-3 py-3 font-black">{item.expense || "-"}</td>
                                            <td className="px-3 py-3 font-black">{formatInr(item.amount)}</td>
                                            <td className="px-3 py-3 font-bold">{item.dueDate ? new Date(item.dueDate).toLocaleDateString("en-IN") : "-"}</td>
                                            <td className="px-3 py-3 font-bold">{item.paidDate ? new Date(item.paidDate).toLocaleDateString("en-IN") : "-"}</td>
                                            <td className="px-3 py-3 font-bold">{item.approvalStatus || "Draft"}</td>
                                            <td className="px-3 py-3 font-bold">{item.paymentStatus || "Pending"}</td>
                                            <td className="px-3 py-3">
                                                {item.paymentProofName || item.paymentProofUrl || item.billName || item.billUrl ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => openDocument({
                                                            title: item.expense || "Other expenditure",
                                                            fileName: item.paymentProofName || item.billName || "Finance PDF",
                                                            url: item.paymentProofUrl || item.billUrl,
                                                        })}
                                                        className="inline-flex items-center gap-2 rounded-xl bg-[var(--accent-primary)] px-3 py-2 text-xs font-black text-white"
                                                    >
                                                        <Eye size={14} />
                                                        View
                                                    </button>
                                                ) : (
                                                    <span className="font-bold text-[var(--text-secondary)]">No proof</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-3 font-bold text-[var(--text-secondary)]">{item.note || "-"}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="9" className="px-3 py-6 text-sm font-bold text-[var(--text-secondary)]">No finance other expenditures added for this month.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel title="Finance Attachments Vault" className="mt-4">
                        <label className="relative mb-5 block max-w-xl">
                            <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input value={vaultSearch} onChange={(event) => setVaultSearch(event.target.value)} placeholder="Search bills, invoices, proofs, receipts, salary exports, student invoices" className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] pl-9 pr-3 text-sm font-bold outline-none" />
                        </label>
                        <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                            {attachments.length ? attachments.map((item) => (
                                <article key={`${item.source}-${item.id}`} className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
                                    <span className="text-[10px] font-black uppercase text-[var(--accent-primary)]">{item.type}</span>
                                    <h3 className="mt-3 text-sm font-black">{item.title}</h3>
                                    <p className="mt-1 text-xs font-bold text-[var(--text-secondary)]">{item.owner} | {item.fileName || "Linked by record"}</p>
                                    <strong className="mt-3 block text-sm font-black">{formatInr(item.amount)}</strong>
                                    <button type="button" onClick={() => openDocument(item)} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--accent-primary)] px-3 py-2 text-xs font-black text-white">
                                        <Eye size={14} />
                                        Open File
                                    </button>
                                </article>
                            )) : (
                                <p className="rounded-xl border border-dashed border-[var(--border-color)] px-4 py-6 text-sm font-bold text-[var(--text-secondary)]">No finance documents found.</p>
                            )}
                        </div>
                    </Panel>

                    <Panel title="Finance Audit History" className="mt-4">
                        <div className="space-y-3">
                            {finance.auditTrail?.length ? finance.auditTrail.slice(0, 12).map((entry, index) => (
                                <article key={`${entry.action}-${entry.createdAt}-${index}`} className="rounded-2xl border border-[var(--border-color)] p-4">
                                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                        <div>
                                            <strong className="text-sm font-black">{entry.action}</strong>
                                            <p className="mt-1 text-xs font-bold text-[var(--text-secondary)]">
                                                {entry.actorName || "User"} | {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString("en-IN") : "-"}
                                            </p>
                                        </div>
                                        <span className="rounded-full bg-[var(--accent-light)] px-3 py-1 text-xs font-black text-[var(--heading-secondary)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]">
                                            {(entry.changes || []).length} change(s)
                                        </span>
                                    </div>
                                    {!!entry.changes?.length && (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                            {entry.changes.slice(0, 8).map((change) => (
                                                <span key={change.field} className="rounded-full bg-[var(--bg-secondary)] px-3 py-1 text-[11px] font-black text-[var(--text-secondary)]">
                                                    {change.field}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </article>
                            )) : (
                                <p className="rounded-xl border border-dashed border-[var(--border-color)] px-4 py-6 text-sm font-bold text-[var(--text-secondary)]">No finance audit history yet.</p>
                            )}
                        </div>
                    </Panel>
                </section>
            </main>
            {pdfPreview && <PdfPreviewModal preview={pdfPreview} onClose={closePdfPreview} />}
        </div>
    );
};

const metricTones = {
    violet: "border-[var(--accent-soft)] bg-[var(--accent-light)] text-violet-800 dark:border-violet-900/40 dark:bg-violet-950/25 dark:text-violet-200",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/25 dark:text-emerald-200",
    amber: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/25 dark:text-amber-200",
    red: "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/25 dark:text-red-200",
    slate: "border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]",
};

const MetricCard = ({ label, value, sub, tone, icon }) => (
    <div className={`min-h-[88px] rounded-2xl border p-4 shadow-sm ${metricTones[tone] || metricTones.slate}`}>
        <div className="flex items-start justify-between gap-3">
            <span className="text-xs font-black uppercase">{label}</span>
            {icon}
        </div>
        <strong className="mt-3 block text-xl font-black">{value}</strong>
        {sub && <p className="mt-1 text-xs font-black opacity-80">{sub}</p>}
    </div>
);

const Panel = ({ title, children, className = "" }) => (
    <section className={`rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm ${className}`}>
        <h2 className="mb-3 text-xl font-black">{title}</h2>
        {children}
    </section>
);

const MonthlyMoneyChart = ({ command }) => {
    const bars = [
        { label: "Money In", amount: Number(command.generatedRevenue || 0), color: "bg-emerald-500" },
        { label: "Money Spent", amount: Number(command.totalSpend || 0), color: "bg-red-500" },
        { label: "Team Amounts", amount: Number(command.teamAllocationFund || 0), color: "bg-violet-500" },
        { label: "Profit / Loss", amount: Number(command.profitLoss || 0), color: Number(command.profitLoss || 0) < 0 ? "bg-red-500" : "bg-emerald-500" },
    ];
    const maxAmount = Math.max(...bars.map((bar) => Math.abs(bar.amount)), 1);
    const spendBreakup = [
        { label: "Team Amounts", amount: Number(command.teamAllocationFund || 0) },
        { label: "Marketing", amount: Number(command.digitalMarketingSpend || 0) },
        { label: "Salary", amount: Number(command.salaryPayout || 0) },
        { label: "Approved Claims", amount: Number(command.approvedClaims || 0) },
        { label: "Other Expenses", amount: Number(command.otherExpenditures || 0) },
    ];

    return (
        <section className="mt-4 grid gap-4 xl:grid-cols-[1fr_0.75fr]">
            <Panel title="This Month Money Chart">
                <div className="space-y-3">
                    {bars.map((bar) => (
                        <div key={bar.label}>
                            <div className="mb-1 flex items-center justify-between gap-3 text-xs font-black">
                                <span>{bar.label}</span>
                                <strong>{formatInr(bar.amount)}</strong>
                            </div>
                            <div className="h-3 overflow-hidden rounded-full bg-[var(--bg-secondary)]">
                                <div className={`h-full rounded-full ${bar.color}`} style={{ width: `${Math.max(4, Math.round((Math.abs(bar.amount) / maxAmount) * 100))}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            </Panel>
            <Panel title="Expense Split">
                <div className="space-y-2">
                    {spendBreakup.map((item) => (
                        <div key={item.label} className="flex items-center justify-between gap-3 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-xs font-black">
                            <span>{item.label}</span>
                            <strong>{formatInr(item.amount)}</strong>
                        </div>
                    ))}
                </div>
            </Panel>
        </section>
    );
};

const AdminAllocationEditor = ({ rows, total, locked, saving, onAdd, onUpdate, onRemove, onSave }) => (
    <div className="mb-3 rounded-2xl border border-[var(--accent-soft)] bg-[var(--accent-light)]/60 p-4 dark:border-[var(--border-color)] dark:bg-[var(--accent-subtle)]">
        <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
                <h3 className="text-sm font-black text-violet-900 dark:text-violet-100">Team Amounts</h3>
                <p className="text-xs font-bold text-[var(--heading-secondary)]/80 dark:text-violet-200/80">
                    Admin can edit the amount given to each team for this month.
                </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-[var(--card-bg)] px-3 py-1 text-xs font-black text-[var(--heading-secondary)] dark:bg-neutral-950 dark:text-[var(--heading-secondary)]">
                    Total {formatInr(total)}
                </span>
                <button type="button" onClick={onAdd} disabled={locked} className="inline-flex h-9 items-center gap-2 rounded-xl border border-[var(--accent-soft)] bg-[var(--card-bg)] px-3 text-xs font-black text-[var(--heading-secondary)] disabled:opacity-50 dark:border-violet-900/50 dark:bg-neutral-950">
                    <Plus size={14} />
                    Add Team
                </button>
                <button type="button" onClick={onSave} disabled={saving || locked} className="inline-flex h-9 items-center gap-2 rounded-xl bg-[var(--accent-hover)] px-3 text-xs font-black text-white disabled:opacity-50">
                    {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
                    Save
                </button>
            </div>
        </div>
        <div className="space-y-2">
            {rows.length ? rows.map((row) => (
                    <div key={row.id} className="grid gap-2 rounded-xl border border-[var(--accent-soft)] bg-[var(--card-bg)] p-2 dark:border-violet-900/40 dark:bg-neutral-950 md:grid-cols-[1fr_0.55fr_0.75fr_1.3fr_auto]">
                        <input value={row.team} onChange={(event) => onUpdate(row.id, "team", event.target.value)} placeholder="Team" disabled={locked} className="h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-xs font-black outline-none disabled:opacity-60" />
                        <input value={row.amount} onChange={(event) => onUpdate(row.id, "amount", event.target.value)} placeholder="Amount" disabled={locked} className="h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-xs font-black outline-none disabled:opacity-60" />
                        <select value={row.approvalStatus || "Draft"} onChange={(event) => onUpdate(row.id, "approvalStatus", event.target.value)} disabled={locked} className="h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-xs font-black outline-none disabled:opacity-60">
                            {["Draft", "Sent for approval", "Approved", "Rejected"].map((option) => <option key={option} value={option}>{option}</option>)}
                        </select>
                        <input value={row.note} onChange={(event) => onUpdate(row.id, "note", event.target.value)} placeholder="Purpose / note" disabled={locked} className="h-10 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 text-xs font-black outline-none disabled:opacity-60" />
                    <button type="button" onClick={() => onRemove(row.id)} disabled={locked} className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-red-200 text-red-600 disabled:opacity-50" aria-label="Remove team amount">
                        <Trash2 size={14} />
                    </button>
                </div>
            )) : (
                <p className="rounded-xl border border-dashed border-[var(--accent-soft)] bg-[var(--card-bg)] px-4 py-4 text-sm font-bold text-[var(--heading-secondary)] dark:border-violet-900/40 dark:bg-neutral-950 dark:text-violet-200">
                    No team amount added yet.
                </p>
            )}
        </div>
    </div>
);

const PendingBox = ({ title, items }) => (
    <div className="mb-4 rounded-2xl border border-[var(--border-color)] p-4 last:mb-0">
        <strong className="text-sm font-black">{title}</strong>
        <div className="mt-3 space-y-2">
            {items.length ? items.slice(0, 6).map((item, index) => (
                <div key={`${title}-${index}`} className="flex justify-between gap-3 text-xs font-bold">
                    <span>{item.title || "Pending item"}</span>
                    <strong>{formatInr(item.amount)}</strong>
                </div>
            )) : (
                <p className="text-sm font-bold text-[var(--text-secondary)]">No pending items.</p>
            )}
        </div>
    </div>
);

const AdvancedFinanceOverview = ({ advanced }) => (
    <section className="mt-4 space-y-4">
        <CompactPanel title="Advanced Finance Health">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {(advanced.executiveKpis || []).map((item) => (
                    <div key={item.label} className="min-h-[96px] rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] px-4 py-3">
                        <span className="text-[10px] font-black uppercase text-[var(--text-secondary)]">{item.label}</span>
                        <strong className="mt-2 block text-lg font-black">
                            {item.format === "currency" ? formatInr(item.value) : `${item.value || 0}${item.unit || ""}`}
                        </strong>
                        <span className="mt-1.5 inline-flex rounded-full bg-[var(--card-bg)] px-2 py-0.5 text-[9px] font-black uppercase text-[var(--accent-primary)]">{item.status}</span>
                    </div>
                ))}
            </div>
            <div className="mt-3 grid gap-3 xl:grid-cols-2">
                <FinanceMiniList title="Receivables" rows={advanced.receivablesPayables?.receivables || []} empty="No open receivables." />
                <FinanceMiniList title="Payables" rows={advanced.receivablesPayables?.payables || []} empty="No pending payables." />
            </div>
        </CompactPanel>

        <CompactPanel title="Forecast, Compliance And Close">
            <div className="grid gap-3 xl:grid-cols-4">
                <ForecastBox rows={advanced.cashFlowForecast || []} />
                <AdminChecklist title="Compliance Checklist" rows={advanced.complianceChecklist || []} />
                <AdminChecklist title="Month Close Checklist" rows={advanced.closeChecklist || []} />
                <AdminRiskRegister rows={advanced.riskRegister || []} />
            </div>
        </CompactPanel>
        <CompactPanel title="Budget Vs Actual Variance">
            <AdminVariance rows={advanced.varianceAnalysis || []} />
        </CompactPanel>
    </section>
);

const CompactPanel = ({ title, children }) => (
    <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-black">{title}</h2>
        {children}
    </section>
);

const ForecastBox = ({ rows }) => (
    <div className="rounded-xl border border-[var(--border-color)] p-3">
        <strong className="text-sm font-black">Cash Flow Forecast</strong>
        <div className="mt-2 space-y-1.5">
            {rows.length ? rows.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 rounded-lg bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[11px] font-black">
                    <span>{row.label}</span>
                    <strong className={row.type === "outflow" ? "text-red-600" : "text-emerald-600"}>{formatInr(row.amount)}</strong>
                </div>
            )) : <p className="text-sm font-bold text-[var(--text-secondary)]">No forecast data.</p>}
        </div>
    </div>
);

const FinanceMiniList = ({ title, rows, empty }) => (
    <div className="rounded-xl border border-[var(--border-color)] p-3">
        <strong className="text-sm font-black">{title}</strong>
        <div className="mt-2 max-h-[142px] space-y-1.5 overflow-y-auto pr-1">
            {rows.length ? rows.map((row, index) => (
                <div key={`${title}-${row.title}-${index}`} className="rounded-lg bg-[var(--bg-secondary)] px-2.5 py-1.5">
                    <div className="flex items-start justify-between gap-3 text-[11px] font-black">
                        <span>{row.title}</span>
                        <strong>{formatInr(row.amount)}</strong>
                    </div>
                    <p className="mt-0.5 text-[10px] font-bold text-[var(--text-secondary)]">{row.owner} | {row.status}</p>
                </div>
            )) : <p className="text-sm font-bold text-[var(--text-secondary)]">{empty}</p>}
        </div>
    </div>
);

const AdminChecklist = ({ title, rows }) => (
    <div className="rounded-xl border border-[var(--border-color)] p-3">
        <strong className="text-sm font-black">{title}</strong>
        <div className="mt-2 space-y-1.5">
            {rows.length ? rows.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 rounded-lg bg-[var(--bg-secondary)] px-2.5 py-1.5 text-[11px] font-black">
                    <span>{row.label}</span>
                    <span className={row.done ? "text-emerald-600" : "text-amber-600"}>{row.detail}</span>
                </div>
            )) : <p className="text-sm font-bold text-[var(--text-secondary)]">No checklist items.</p>}
        </div>
    </div>
);

const AdminRiskRegister = ({ rows }) => (
    <div className="rounded-xl border border-[var(--border-color)] p-3">
        <strong className="text-sm font-black">Risk Register</strong>
        <div className="mt-2 max-h-[188px] space-y-1.5 overflow-y-auto pr-1">
            {rows.length ? rows.map((row, index) => (
                <div key={`${row.title}-${index}`} className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-black text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/25 dark:text-amber-200">
                    <div className="flex items-center justify-between gap-3">
                        <span>{row.title}</span>
                        <span>{row.severity}</span>
                    </div>
                    <p className="mt-0.5 text-[10px] font-bold">Impact: {typeof row.impact === "number" && row.impact > 999 ? formatInr(row.impact) : row.impact}</p>
                </div>
            )) : <p className="text-sm font-bold text-[var(--text-secondary)]">No major finance risks.</p>}
        </div>
    </div>
);

const AdminVariance = ({ rows }) => (
    <div className="overflow-x-auto">
            <table className="min-w-[620px] w-full text-left text-xs">
                <thead className="text-[var(--text-secondary)]">
                    <tr className="border-b border-[var(--border-color)]">
                        {["Department", "Budget", "Actual", "Variance", "Use"].map((head) => (
                            <th key={head} className="px-3 py-2 font-black uppercase">{head}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length ? rows.slice(0, 8).map((row) => (
                        <tr key={row.department} className="border-b border-[var(--border-color)]/70">
                            <td className="px-3 py-2 font-black">{row.department}</td>
                            <td className="px-3 py-2 font-bold">{formatInr(row.budget)}</td>
                            <td className="px-3 py-2 font-bold">{formatInr(row.actualSpend)}</td>
                            <td className={`px-3 py-2 font-black ${row.variance < 0 ? "text-red-600" : "text-emerald-600"}`}>{formatInr(row.variance)}</td>
                            <td className="px-3 py-2 font-bold">{row.utilization}%</td>
                        </tr>
                    )) : (
                        <tr>
                            <td colSpan="5" className="px-3 py-4 text-sm font-bold text-[var(--text-secondary)]">No variance records.</td>
                        </tr>
                    )}
                </tbody>
            </table>
    </div>
);

const PdfPreviewModal = ({ preview, onClose }) => (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
        <section className="flex h-[88vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl">
            <header className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
                <div className="min-w-0">
                    <h2 className="truncate text-base font-black">{preview.title}</h2>
                    <p className="text-xs font-bold text-[var(--text-secondary)]">PDF preview</p>
                </div>
                <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[var(--border-color)] text-[var(--text-primary)] transition hover:bg-[var(--bg-secondary)]" aria-label="Close PDF preview">
                    <X size={18} />
                </button>
            </header>
            <iframe title={preview.title} src={preview.url} className="h-full w-full flex-1 bg-[var(--card-bg)]" />
        </section>
    </div>
);

export default FinanceCommand;


